import { ApplicationConfig, importProvidersFrom } from '@angular/core';
import { provideRouter } from '@angular/router';

import { KeplerDbInterceptor, KeplerSfdcInterceptor } from '@/shared/interceptors';
import { APP_BASE_HREF } from '@angular/common';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { environment } from '@environments/environment';
import { OktaAuthModule } from '@okta/okta-angular';
import OktaAuth from '@okta/okta-auth-js';
import { GknRoutePath, routes } from './app.routes';

/**
 * Prefer `localStorage` to enable auto-login
 *
 * The following values for storageType are recognized:
 *  - `memory`: values are stored in a closure and will not survive a page reload
 *  - `sessionStorage`: will only be available to the current browser tab.
 *    this means OktaAuth requires the user to still initiate a login flow
 *    with a button click from your app.
 *  - `localStorage`: available to all browser tabs. effectively enables
 *    OktaAuthStateManager to detect login without user interaction.
 *  - `cookie`: available to all browser tabs, and server-side code, but
 *    effectively breaks login for any browser blocking third-party cookies, like Safari.
 *
 * Tokens should be safe in localStorage from XSS attacks because the app's
 * Content Security Policy in customHttp.yml is restrictive (allowlist).
 */
const storageType = 'localStorage';

const oktaAuth = new OktaAuth({
    issuer: environment.oidc.issuer,
    clientId: environment.oidc.clientId,
    redirectUri: environment.oidc.redirectUri,
    scopes: environment.oidc.scopes,
    tokenManager: {
        storage: storageType,
    },
    storageManager: {
        transaction: { storageType },
        cache: { storageType },
        token: { storageType },
    },
    cookies: {
        secure: true, // cookies can be the fallback when local/session storage are not available, so ensure they are secure.
    },
    restoreOriginalUri: async (oktaAuth, originalUri) => {
        // Get all valid route paths from the enum
        const allowedPaths = Object.values(GknRoutePath).map((path) => `/${path}`);

        const isValidPath = (path: string): boolean => {
            const cleanPath = path.split('?')[0].split('#')[0];

            // Check if path matches any allowed route or is a sub-route
            return (
                cleanPath === '/' ||
                cleanPath === '/home' ||
                allowedPaths.some((allowed) => cleanPath === allowed || cleanPath.startsWith(allowed + '/'))
            );
        };

        const isValidUrl = (url: string): boolean => {
            try {
                const urlObj = new URL(url);
                return urlObj.origin === window.location.origin && isValidPath(urlObj.pathname);
            } catch {
                return false;
            }
        };

        let targetUri = '/home';

        if (originalUri && isValidPath(originalUri)) {
            // Keep the full originalUri including query params
            targetUri = originalUri;
        } else if (document.referrer && isValidUrl(document.referrer)) {
            // Keep full URL including query params from referrer
            const referrerUrl = new URL(document.referrer);
            targetUri = referrerUrl.pathname + referrerUrl.search + referrerUrl.hash;
        } else if (window.location.pathname !== '/login/callback' && isValidPath(window.location.pathname)) {
            // Keep current URL with query params
            targetUri = window.location.pathname + window.location.search + window.location.hash;
        }

        window.location.assign(targetUri);
    },
    transformAuthState: async (oktaAuth, authState) => {
        if (!authState.isAuthenticated && authState.error) {
            console.error('Auth error:', authState.error);
        }
        return authState;
    },
});

export const appConfig: ApplicationConfig = {
    providers: [
        provideRouter(routes),
        provideHttpClient(withInterceptors([KeplerDbInterceptor, KeplerSfdcInterceptor])),
        importProvidersFrom(OktaAuthModule.forRoot({ oktaAuth })),
        {
            provide: APP_BASE_HREF,
            useValue: environment.appBaseHref,
        },
    ],
};
